import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Typography,
} from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { NOTE_TYPES } from './fields';
import {
  ADD_INVESTMENT_UPDATE,
  REMOVE_INVESTMENT_UPDATE,
  UPDATE_INVESTMENT_UPDATE,
} from '../../pages/apollo_mutations';

const { TextArea } = Input;
const { Text } = Typography;

// UPDATE TIMELINE — what the team tells an investor ABOUT a note after it is listed ("second draw
// disbursed", "roof finished", "borrower is refinancing"). It is the note's own trail, stored on the note
// and painted by the investor dashboard under Docs & Updates; this is where the operator writes it.
//
// Three verbs on one entry (backend: investment/modules/admin § timeline.service): add, edit, remove. All
// three answer with the NOTE, so nothing is re-sorted here — the backend keeps the array newest-first by
// the date the thing HAPPENED and the list simply re-renders from the answer (Apollo normalises the note
// by `_id`, so the `getInvestment` on screen gets the new array).
//
// WHO gets the buttons (TL, 2026-09-12): only the products that report progress on a deal — the
// Individual Note and the Rolling Note. A Constitution Note and the Credit Fund keep the read-only trail:
// they are not construction, there are no draws and no inspections to report, and their pages are PRODUCT
// pages, not per-note fact sheets.
const WRITABLE_ON = [NOTE_TYPES.individual, NOTE_TYPES.rolling];

// A CLOSED list (TL, 2026-09-12). It used to be free text, and the vocabulary drifted for nothing:
// measured on the data, these five are every word in use (plus a stray `Test`, which is not offered),
// and the investor's fact sheet only distinguishes `Inspection` — it gets its own icon and every other
// kind is painted the same. The backend still takes any wording; the panel simply stops inventing it.
const UPDATE_TYPES = ['Payments', 'Inspection', 'Draw Sent', 'Update', 'Borrower Looking to Refinance'];

// The backend's copy, verbatim: INVESTMENT_UPDATE_NOT_FOUND / INVESTMENT_UPDATE_NO_CHANGES and the field
// rules (empty box, 120 / 2000 characters) each say what to do about it better than a generic line here.
const backendMessage = (error) => error.message.replace(/^GraphQL error: /, '');

export function isTimelineWritable(noteType) {
  return WRITABLE_ON.includes(noteType);
}

// An entry already SAVED with a word outside the list keeps it: its own value joins the options while it
// is being edited, so opening an old entry never silently rewrites its type into one of the five.
function typeOptions(entry) {
  const stored = entry && entry.type;
  const values =
    stored && !UPDATE_TYPES.includes(stored) ? [...UPDATE_TYPES, stored] : UPDATE_TYPES;
  return values.map((value) => ({ value, label: value }));
}

// Only what the operator MOVED travels on an edit: the backend applies a box only when it arrives, so an
// untouched field is never restated, and an edit that changed nothing comes back as
// INVESTMENT_UPDATE_NO_CHANGES instead of saving a no-op.
function editedFields(values, entry) {
  const patch = {};
  if (values.type !== entry.type) patch.type = values.type;
  if (values.info !== entry.info) patch.info = values.info;
  if (!moment(values.date).isSame(moment(entry.date))) {
    patch.date = moment(values.date).toISOString();
  }
  return patch;
}

// One modal for both verbs: empty for the add, preloaded for the edit. Nothing here validates the two
// text boxes — the backend owns that rule (trimmed, non-empty, 120 / 2000) and its refusal is the copy
// the operator reads. The date is the exception: it is a REQUIRED variable of the add, so a missing one
// would come back as a schema error instead of a sentence.
function UpdateModal({ entry, saving, onCancel, onSave }) {
  const [form] = Form.useForm();
  const editing = !!entry;

  return (
    <Modal
      visible
      title={editing ? 'Edit update' : 'Add update'}
      okText={editing ? 'Save update' : 'Add update'}
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={
          editing
            ? { type: entry.type, info: entry.info, date: entry.date ? moment(entry.date) : null }
            : { type: undefined, info: '', date: moment() }
        }
        onFinish={onSave}
      >
        <Form.Item
          name="type"
          label="Type"
          extra="What kind of news this is. Investors only see the kind on an Inspection, which gets its own icon."
        >
          <Select
            options={typeOptions(entry)}
            placeholder="Pick the kind of update"
            allowClear
          />
        </Form.Item>
        <Form.Item
          name="info"
          label="Text"
          extra="What investors read on the note's Docs & Updates section."
        >
          <TextArea rows={4} placeholder="What happened" />
        </Form.Item>
        <Form.Item
          name="date"
          label="Date"
          rules={[{ required: true, message: 'The update needs the date it happened' }]}
          extra="The day the thing happened — the timeline is ordered by it, newest first."
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function NoteUpdates({ investment, writable }) {
  // `null` = closed, `{}` = adding, an entry = editing it.
  const [editor, setEditor] = useState(null);
  const [add, addState] = useMutation(ADD_INVESTMENT_UPDATE);
  const [edit, editState] = useMutation(UPDATE_INVESTMENT_UPDATE);
  const [remove] = useMutation(REMOVE_INVESTMENT_UPDATE);

  const investmentId = investment && investment._id;
  const updates = (investment && investment.updates) || [];
  const saving = addState.loading || editState.loading;

  const onSave = async (values) => {
    const editing = editor && editor._id;
    try {
      if (editing) {
        const data = editedFields(values, editor);
        if (isEmpty(data)) {
          // The same refusal the backend would answer with, without the round trip.
          message.info('Nothing changed on that update.');
          return;
        }
        await edit({
          variables: { data: { investmentId, updateId: editor._id, ...data } },
        });
        message.success('Update saved');
      } else {
        await add({
          variables: {
            data: {
              investmentId,
              type: values.type,
              info: values.info,
              date: moment(values.date).toISOString(),
            },
          },
        });
        message.success('Update added');
      }
      setEditor(null);
    } catch (error) {
      // The modal stays open on a refusal: what it holds is what has to be corrected.
      message.error(backendMessage(error), 8);
    }
  };

  const onRemove = async (updateId) => {
    try {
      await remove({ variables: { data: { investmentId, updateId } } });
      message.success('Update removed');
    } catch (error) {
      message.error(backendMessage(error), 8);
    }
  };

  return (
    <div>
      {writable && (
        <Button
          type="dashed"
          htmlType="button"
          icon={<PlusOutlined />}
          onClick={() => setEditor({})}
          style={{ marginBottom: 8 }}
        >
          Add update
        </Button>
      )}

      {isEmpty(updates) ? (
        <div style={{ color: '#95a5a6' }}>No updates yet</div>
      ) : (
        updates.map((item) => (
          <div key={item._id} style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <strong>{item.type}</strong>
              {item.date && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {moment(item.date).format('L')}
                </Text>
              )}
              {writable && (
                <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  <Button
                    type="link"
                    size="small"
                    htmlType="button"
                    onClick={() => setEditor(item)}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Remove this update? Investors stop seeing it on the note."
                    okText="Remove"
                    cancelText="Cancel"
                    onConfirm={() => onRemove(item._id)}
                  >
                    <Button type="link" size="small" danger htmlType="button">
                      Remove
                    </Button>
                  </Popconfirm>
                </span>
              )}
            </div>
            <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{item.info}</div>
          </div>
        ))
      )}

      {writable && editor && (
        // Remounted per entry so the boxes are preloaded with the row the operator picked.
        <UpdateModal
          key={editor._id || 'new'}
          entry={editor._id ? editor : null}
          saving={saving}
          onCancel={() => setEditor(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}
